import { Query, useQuery, type QueryKey, type UseQueryReturnType } from '@tanstack/vue-query'
import { apiInstance, type ResponseType, type ReturnResponseFor } from '@/services/apiService'
import type { Response } from '@/types/response'
import { computed, toValue, type MaybeRef } from 'vue'

interface UseApiParams<Data, TResponseType extends ResponseType> {
  modus: string
  payload?: MaybeRef<Record<string, unknown>>
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  responseType?: TResponseType
  keys?: MaybeRef<QueryKey>
  enabled?: MaybeRef<boolean>
  onSuccess?: (data: ReturnResponseFor<TResponseType, Response<unknown>>) => void
  onError?: (error: Error, query: Query<Data, Error, Data, QueryKeys>) => void
  staleTime?: number
}

// TODO: better typings + error handling + try only on non understandable error and not on every request
// maybe use generic type for response data
// and infer the response type based on modus
// e.g. if modus is 'user', then response type is User
// if modus is 'template', then response type is Template, etc.
// or use a map of modus to response type
// e.g. const modusResponseMap = { user: User, template: Template, ... }
// then use modusResponseMap[modus] to get the response type
// and use that type in the useApiMutation function
// e.g. useApiMutation<T = typeof modusResponseMap[modus]>({ modus, ... })
// This would require defining all the response types and the map
// which could be a lot of work but would provide strong typing
// and better developer experience

type QueryKeys = readonly [
  string,
  {
    readonly method: 'GET' | 'POST'
    readonly payload: Record<string, unknown> | undefined
    readonly keys: readonly unknown[] | undefined
  },
]

export function useApiQuery<Data = unknown>(
  params: UseApiParams<Data, 'json'>,
): UseQueryReturnType<Data, Error>

export function useApiQuery<TResponseType extends Exclude<ResponseType, 'json'>>(
  params: UseApiParams<ReturnResponseFor<TResponseType>, TResponseType>,
): UseQueryReturnType<ReturnResponseFor<TResponseType>, Error>

export function useApiQuery<TResponseType extends ResponseType, Data>({
  modus,
  payload,
  method = 'POST',
  headers = {},
  responseType = 'json' as TResponseType,
  keys,
  enabled,
  onError,
  onSuccess,
  staleTime,
}: UseApiParams<Data, TResponseType>) {
  const queryKey = computed(
    () => [modus, { method, payload: toValue(payload), keys: toValue(keys) }] as const,
  )

  return useQuery<Data, Error, Data, QueryKeys>({
    queryKey,
    queryFn: async ({ signal }) => {
      const apiService = apiInstance
      if (!apiService.baseUrl.startsWith('/') && !apiService.baseUrl.startsWith('http')) {
        apiService.baseUrl = '/' + apiService.baseUrl
      }
      const response = await apiService.request<
        typeof responseType,
        ReturnResponseFor<TResponseType, Response<Data>>
      >(modus, toValue(payload), method, headers, responseType, signal)
      if (responseType === 'json') {
        const { Inhalt, Modus, Status } = response as Response<Data>
        if (Status === 'ok') {
          onSuccess?.(Inhalt as ReturnResponseFor<TResponseType>)
          return Inhalt
        }
        throw new Error(`${Status}|${Modus}|${Inhalt}` || 'Unknown error')
      } else {
        onSuccess?.(response as ReturnResponseFor<TResponseType>)
        return response as Data
      }
    },
    retry: (failureCount, error) => {
      console.log({ failureCount, error })
      return (
        !error.message.startsWith('signal|') &&
        !error.message.startsWith('expired|') &&
        !error.message.startsWith('fehler|') &&
        failureCount < 3
      )
    },
    enabled,
    throwOnError: (error, query) => {
      onError?.(error, query)
      return false
    },
    staleTime,
  })
}
